// ─── Arbre de CONTENU d'un lieu (récursif, comme MoPCal) ──────────
// Extrait d'EcranPlans (découpage). Lieu → sous-lieux → éléments → articles,
// avec compteurs agrégés et accordéons. En session de comptage, chaque article
// se pointe ✓/✗ (le ✗ ouvre la saisie du motif via onMotif).
import React, { useMemo } from 'react';
import { View, Text, TouchableOpacity, Image } from 'react-native';
import { useUI, useT } from '@mop/core';
import { noeudParId, articlesDe } from './services/inventaires';
import { makeStylesPlans } from './stylesPlans';

const HIT = { top: 8, bottom: 8, left: 8, right: 8 };

export default function ArbreContenu(props) {
  const {
    nodeId, prof = 0, racine = false, onFermer,
    emplacements, compteDe, elementsDe, masquerVides, contPlies, toggleCont,
    entrerLieu, session, pointes, pointer, onMotif, onFiche,
  } = props;
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStylesPlans(UI), [UI]);

  const node = noeudParId(emplacements, nodeId);
  if (!node) return null;
  const total = compteDe(nodeId);
  // « Masquer vides » s'applique AUSSI aux sous-lieux du lieu racine.
  const sousLieux = (node.enfants || []).filter(e => !masquerVides || compteDe(e.id) > 0);
  const elems = elementsDe(nodeId);
  const plie = !racine && contPlies.has(nodeId);
  return (
    <View>
      <TouchableOpacity
        style={[s.contLieu, { marginLeft: prof * 14 }, racine && s.contLieuRacine]}
        activeOpacity={0.7}
        onPress={() => (racine ? null : toggleCont(nodeId))}
        onLongPress={() => entrerLieu(nodeId)} delayLongPress={450}>
        {!racine && <Text style={s.contChev}>{plie ? '▸' : '▾'}</Text>}
        <Text style={s.contEmoji}>{node.emoji || '📍'}</Text>
        <Text style={[s.contNom, racine && s.contNomRacine]} numberOfLines={1}>{node.nom}</Text>
        <Text style={s.contBadge}>{total}</Text>
        {racine && (
          <>
            <TouchableOpacity hitSlop={HIT} onPress={() => entrerLieu(nodeId)} style={{ marginLeft: 6 }}>
              <Text style={s.contIc}>⛶</Text>
            </TouchableOpacity>
            <TouchableOpacity hitSlop={HIT} onPress={onFermer} style={{ marginLeft: 8 }}>
              <Text style={s.contIc}>✕</Text>
            </TouchableOpacity>
          </>
        )}
      </TouchableOpacity>
      {!plie && (
        <>
          {sousLieux.map(sl => <ArbreContenu {...props} key={sl.id} nodeId={sl.id} prof={prof + 1} racine={false} />)}
          {elems.map(g => {
            const cle = `el:${g.id}:${nodeId}`;
            const ouvElem = !contPlies.has(cle);   // articles visibles par défaut
            const totalG = g.arts.reduce((a, x) => a + x.qte, 0);
            return (
              <View key={g.id}>
                {/* Ligne ÉLÉMENT : dépliable vers ses ARTICLES rangés ici. */}
                <TouchableOpacity style={[s.contElem, { marginLeft: (prof + 1) * 14 }]}
                  activeOpacity={0.7} onPress={() => toggleCont(cle)}>
                  <Text style={s.contChev}>{ouvElem ? '▾' : '▸'}</Text>
                  <Text style={{ fontSize: 15 }}>{g.emoji}</Text>
                  <Text style={s.contElemNom} numberOfLines={1}>{g.nom}</Text>
                  <Text style={s.contElemQte}>{totalG}</Text>
                </TouchableOpacity>
                {ouvElem && g.arts.map(a => (
                  // Indenté au-delà du nom de l'élément + miniature à gauche.
                  // Tap = fiche info de l'article (sauf en session, où l'on pointe).
                  <TouchableOpacity key={a.id} style={[s.contArt, { marginLeft: (prof + 2) * 14 + 20 }]}
                    activeOpacity={0.7} disabled={session}
                    onPress={() => {
                      const idx = articlesDe(g.item).findIndex(x => x.id === a.id);
                      onFiche(g.item, Math.max(0, idx));
                    }}>
                    {a.photo
                      ? <Image source={{ uri: a.photo.url }} style={s.contArtPhoto} />
                      : <Text style={{ fontSize: 13 }}>🏷️</Text>}
                    <Text style={s.contArtNom} numberOfLines={1}>{a.nom}</Text>
                    {session ? (
                      <>
                        <TouchableOpacity style={[s.point, pointes[a.id] === 'present' && s.pointOk]} onPress={() => pointer(a.id, 'present')}>
                          <Text style={[s.pointTxt, pointes[a.id] === 'present' && s.pointTxtOk]}>✓</Text>
                        </TouchableOpacity>
                        <TouchableOpacity style={[s.point, pointes[a.id] === 'absent' && s.pointKo]}
                          onPress={() => { pointer(a.id, 'absent'); onMotif(a); }}>
                          <Text style={[s.pointTxt, pointes[a.id] === 'absent' && s.pointTxtKo]}>✗</Text>
                        </TouchableOpacity>
                      </>
                    ) : (
                      <Text style={s.contArtQte}>{a.unique ? '1' : a.qte}</Text>
                    )}
                  </TouchableOpacity>
                ))}
              </View>
            );
          })}
          {!racine && sousLieux.length === 0 && elems.length === 0 && (
            <Text style={[s.contVide, { marginLeft: (prof + 1) * 14 }]}>{t('inventaire.modeleVide')}</Text>
          )}
        </>
      )}
    </View>
  );
}
